// oxlint-disable use-unknown-in-catch-callback-variable
import { mimeTypes } from '@/shared/constants';
import type { DriveFile, DriveMimeType } from '@/shared/DriveTypes';
import { type PopupState } from 'material-ui-popup-state/hooks';
import { useEffect } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { useLocation } from 'wouter';
import * as DriveAPI from '~/utils/DriveAPI';
import { concatTo } from '~/utils/fast';
import { getPrevNextSibling } from '~/utils/getPrevNext';
import { keys, useAuth, useKeyPress, useMutateFiles, useUserFiles, useUserFilesData, useWikisMutate } from '~/utils/hooks/hooks';
import { hasIndex, sort } from '~/utils/sort';
import { useOpenId, useStore } from '~/utils/store';
import { FixedSizeMap, getId, getNewFile, getNewFileId, isAFolder, MutationQueue, omit } from '~/utils/utils';
import getAllAncestors from './getAllAncestors';
import getAllChildren from './getAllChildren';
import getMovedFiles, { type Direction } from './getMovedFiles';

const pathMap = new FixedSizeMap<string, string>(10);
export const iconTypes = {
  add: 'add',
  more: 'more',
};

export const useCreate = ({ files, wikiId }: { files: DriveFile[]; wikiId: string | undefined }) => {
  const { mutate: mutateFiles } = useMutateFiles(wikiId);
  const { mutate } = useSWRConfig();
  const { data: user } = useAuth();
  const { data: userFiles = [], mutate: mutateUserFiles } = useUserFiles();
  const navigate = useLocation()[1];
  const setNewFile = useStore((state) => state.setNewFile);
  const setToast = useStore((s) => s.setToast);

  return (
    properties: Parameters<typeof DriveAPI.create>[0] & {
      shortcutDetails?: {
        targetId: string;
        targetMimeType?: DriveMimeType;
      };
    }
  ) => {
    const newFile = getNewFile({
      ...properties,
      user,
    });
    const optimisticData = [...files, newFile];
    setNewFile(newFile);
    navigate(`/app/page/${wikiId}/${newFile.id}`);
    void mutateFiles(
      async (files: DriveFile[] = []) => {
        const res = await DriveAPI.create(properties)
          .catch((err: { result: { error: { code: number; status: string; message: string } } } | Error) => {
            setNewFile(null);
            navigate(`/app/page/${wikiId}/${properties.parents[0]}`);
            if (err instanceof Error) throw err;
            const { error } = err.result;
            if (error.code === 404 || error.code === 403) {
              setToast({
                type: 'error',
                message: 'File needs to be shared with wiki@youneedawiki.com',
              });
            }
            return Promise.reject(error);
          })
          .finally(() => {
            const store = useStore.getState();
            if (store.newFile?.id === newFile.id) setNewFile(null);
          });
        void mutate(`${keys.file}${res.id}`, res, false);
        void mutate(keys.export.replace('{id}', res.id), { html: '<html></html>' }, false);
        void mutateUserFiles(concatTo(userFiles, res), false);

        const newFilePath = `/app/page/${wikiId}/${newFile.id}`;
        const realFilePath = `/app/page/${wikiId}/${res.id}`;
        pathMap.set(newFilePath, realFilePath);

        const path = window.location.pathname;
        const nextPath = path === newFilePath ? realFilePath : pathMap.get(path);
        if (nextPath) {
          const store = useStore.getState();
          if (store.openId === newFile.id) store.setOpenId(res.id, store.menuType);
          if (store.modalId === newFile.id) store.setModalId(res.id);
          // double queueMicrotask is required to address issue where user has a menu open on the new file
          // see useLayoutEffect in Tree.tsx for more details
          queueMicrotask(() =>
            queueMicrotask(() => {
              navigate(nextPath, {
                replace: true,
                state: {
                  revalidate: false,
                },
              });
            })
          );
        }
        return concatTo(files, res);
      },
      {
        optimisticData,
        revalidate: false,
      }
    );
  };
};

export const useCopy = ({
  file,
  files,
  wikiId,
}: {
  file: DriveFile | undefined;
  files: DriveFile[];
  wikiId: string | undefined;
}) => {
  const { mutate: mutateFiles } = useMutateFiles(wikiId);
  const { cache, mutate } = useSWRConfig();
  const { data: userFiles = [], mutate: mutateUserFiles } = useUserFiles();
  const setNewFile = useStore((state) => state.setNewFile);
  const navigate = useLocation()[1];

  return () => {
    if (!file) throw new Error('file not found');
    const name = `Copy of ${file.name}`;
    if (!file.parents) throw new Error('file parents not found');
    const newFile = { ...(omit(file, 'capabilities') as DriveFile), name, id: getNewFileId() };
    setNewFile(newFile);
    navigate(`/app/page/${wikiId}/${newFile.id}`);
    // note: not using isAFolder as shortcuts can be copied w/ a single operation
    const children = file.mimeType === mimeTypes.folder ? getAllChildren(files, file.id) : [];
    const useClient =
      userFiles.some((o) => o.id === file.id) &&
      (children.length > 0 ? children.every((o) => userFiles.some((f) => f.id === o.id)) : true);

    void mutateFiles(
      (files: DriveFile[] = []) =>
        isAFolder(file)
          ? DriveAPI.copyAll({
              folder: file,
              files: children,
              useClient,
            })
              .then((res) => {
                const folder = res[0];
                if (!folder) throw new Error('Folder not copied');
                void mutate(`${keys.file}${folder.id}`, folder, false);
                if (useClient) void mutateUserFiles(concatTo(userFiles, res), false);

                const newFilePath = `/app/page/${wikiId}/${newFile.id}`;
                const realFilePath = `/app/page/${wikiId}/${folder.id}`;
                pathMap.set(newFilePath, realFilePath);

                const currentPath = window.location.pathname;
                const mappedPath = pathMap.get(currentPath);

                if (currentPath === newFilePath || mappedPath) {
                  const store = useStore.getState();
                  if (store.openId === newFile.id) store.setOpenId(folder.id, store.menuType);
                  if (store.modalId === newFile.id) store.setModalId(folder.id);
                  navigate(`/app/page/${wikiId}/${folder.id}`, {
                    replace: true,
                    state: {
                      revalidate: false,
                    },
                  });
                  if (mappedPath) pathMap.delete(currentPath);
                }
                return concatTo(files, res);
              })
              .finally(() => {
                const store = useStore.getState();
                if (store.newFile?.id === newFile.id) setNewFile(null);
              })
          : DriveAPI.copy({
              id: file.id,
              name,
              parents: file.parents as [string],
              useClient,
            })
              .then((res) => {
                const cachedExport = cache.get(keys.export.replace('{id}', file.id))?.data as { html: string };
                void mutate(`${keys.file}${res.id}`, res, false);
                void mutate(keys.export.replace('{id}', res.id), cachedExport ?? { html: '<html></html>' }, false);
                if (useClient) void mutateUserFiles(concatTo(userFiles, res), false);

                const newFilePath = `/app/page/${wikiId}/${newFile.id}`;
                const realFilePath = `/app/page/${wikiId}/${res.id}`;
                pathMap.set(newFilePath, realFilePath);

                const currentPath = window.location.pathname;
                const mappedPath = pathMap.get(currentPath);

                if (currentPath === newFilePath || mappedPath) {
                  const store = useStore.getState();
                  if (store.openId === newFile.id) store.setOpenId(res.id, store.menuType);
                  if (store.modalId === newFile.id) store.setModalId(res.id);
                  navigate(`/app/page/${wikiId}/${res.id}`, {
                    replace: true,
                    state: {
                      revalidate: false,
                    },
                  });
                  if (mappedPath) pathMap.delete(currentPath);
                }
                return concatTo(files, res);
              })
              .finally(() => {
                const store = useStore.getState();
                if (store.newFile?.id === newFile.id) setNewFile(null);
              }),
      {
        optimisticData: [...files, newFile],
        revalidate: false,
      }
    );
  };
};

export const useMutateFile = ({
  file,
  files,
  wiki,
}: {
  file: DriveFile | undefined;
  files: DriveFile[];
  wiki: DriveFile | undefined;
}) => {
  const { mutate } = useSWRConfig();
  const { data: wikis = [], mutate: mutateWikis } = useWikisMutate();
  const { data: userFiles = [] } = useUserFilesData();
  const setStatus = useStore((state) => state.setStatus);
  const navigate = useLocation()[1];
  /**
   * update properties of current file or update a specific file if id is provided
   */
  return (properties: Partial<Omit<Parameters<typeof DriveAPI.update>[0], 'id'> & { id?: string }>) => {
    if (!file) throw new Error('file not defined');
    const isRenaming = 'name' in properties && Object.keys(properties).length === 1;
    const optimisticData: Partial<DriveFile> = {
      ...(!properties.id && { ...file }),
      ...omit(properties, 'addParents', 'removeParents', 'fields'),
      ...(properties.addParents && ({ parents: [properties.addParents] } satisfies Partial<DriveFile>)),
    };
    const id = properties.id ?? file.id;
    if (isRenaming) setStatus('loading', { id });
    const operation = mutate(
      `${keys.file}${id}`,
      async () => {
        if (properties.trashed) {
          const { prev } = getPrevNextSibling({ file, files });
          const to = prev?.id ?? file.parents?.[0] ?? wiki?.id;
          navigate(`/app/page/${wiki?.id}/${to}`);
        }
        return DriveAPI.update({
          id,
          ...(properties.addParents &&
            file.parents?.[0] && {
              removeParents: file.parents[0],
            }),
          ...properties,
          useClient: userFiles.some((o) => o.id === id),
        })
          .catch((err: { result: { error: { code: number; status: string; message: string } } } | Error) => {
            if (err instanceof Error) throw err;
            const { error } = err.result;
            if (error.code === 500 && error.status === 'UNKNOWN') return file;
            throw new Error(error.message);
          })
          .finally(() => (isRenaming ? setStatus('idle', { id }) : undefined));
      },
      { optimisticData, revalidate: false, populateCache: true }
    );

    const nextFiles =
      'trashed' in optimisticData
        ? optimisticData.trashed
          ? files.filter((o) => o.id !== optimisticData.id)
          : files.map((o) => (o.id === optimisticData.id ? optimisticData : o))
        : files.map((o) => (o.id === optimisticData.id ? optimisticData : o));

    void mutate(
      `${keys.files}${wiki?.id}`,
      operation.then(() => nextFiles),
      {
        optimisticData: nextFiles,
        revalidate: false,
      }
    );

    if (!properties.id && file.id === wiki?.id && !optimisticData.trashed && wikis.length > 0) {
      const nextWikis = wikis.map((w) => (w.id === file.id ? { ...w, ...optimisticData } : w)) as DriveFile[];
      void mutateWikis(operation.then(() => nextWikis), { optimisticData: nextWikis, revalidate: false });
    }

    return operation;
  };
};

const getResetMessage = (current: number, total: number) =>
  total > 1 ? `Resetting all positions... (${current}/${total})` : 'Resetting all positions...';

const mutationQueue = new MutationQueue();

export const useMove = ({
  files,
  file,
  wiki,
  onMove,
}: {
  files: DriveFile[];
  file: DriveFile | undefined;
  wiki: DriveFile;
  onMove?: () => void;
}) => {
  const { mutate: mutateFiles } = useMutateFiles(wiki.id);
  const { mutate } = useSWRConfig();
  const setToast = useStore((s) => s.setToast);
  const setStatus = useStore((state) => state.setStatus);

  return (direction: Direction | 'reset') => {
    if (!file) throw new Error('openFile not found');
    onMove?.();

    if (direction === 'reset') {
      // reset all
      if (file.id === wiki.id) {
        const indexedFiles = files.filter(hasIndex);
        const total = indexedFiles.length;
        let proceed = true;
        let current = 0;

        for (const f of indexedFiles) {
          if (!proceed) return;
          setToast({
            message: getResetMessage(++current, total),
            actionLabel: 'Cancel',
            action: () => (proceed = false),
            autoHideDuration: null,
          });
          const properties = { ...f.properties, index: null };
          void mutate(
            `${keys.file}${f.id}`,
            () =>
              DriveAPI.update({
                id: f.id,
                properties,
              }),
            {
              optimisticData: { ...f, properties },
              revalidate: false,
            }
          );
        }
        setToast('Reset all positions');

        const nextFiles = files.map((o) =>
          o.properties?.index ? { ...o, properties: { ...o.properties, index: null } } : o
        );
        void mutateFiles(Promise.resolve(nextFiles), {
          optimisticData: nextFiles,
          revalidate: false,
        });
        return;
      }

      const properties = { ...file.properties, index: null };
      const nextFile = {
        ...file,
        properties,
      };
      const operation = mutate(
        `${keys.file}${file.id}`,
        () =>
          DriveAPI.update({
            id: file.id,
            properties,
          }),
        {
          optimisticData: nextFile,
          revalidate: false,
        }
      );

      const nextFiles = files.map((o) => (o.id === file.id ? nextFile : o));
      void mutateFiles(
        operation.then(() => nextFiles),
        {
          optimisticData: nextFiles,
          revalidate: false,
        }
      );
      return;
    }

    const { nextFile, nextSiblingFile, nextFiles } = getMovedFiles({ files, file, direction });
    const siblingFile = files.find((o) => o.id === nextSiblingFile?.id);
    const modifySibling = nextSiblingFile && siblingFile?.properties?.index !== nextSiblingFile.properties?.index;
    const toMove = file && file.properties?.index !== nextFile.properties?.index;
    if (toMove) setStatus('loading', { id: file.id });

    const operations = Promise.all([
      toMove
        ? mutationQueue.run(`${keys.file}${nextFile.id}`, () =>
            mutate(
              `${keys.file}${nextFile.id}`,
              async () =>
                DriveAPI.update({
                  id: nextFile.id,
                  properties: nextFile.properties,
                }).finally(() => setStatus('idle', { id: file.id })),
              {
                optimisticData: nextFile,
                revalidate: false,
              }
            )
          )
        : undefined,
      modifySibling
        ? mutationQueue.run(`${keys.file}${nextSiblingFile.id}`, () =>
            mutate(
              `${keys.file}${nextSiblingFile.id}`,
              DriveAPI.update({
                id: nextFile.id,
                properties: nextFile.properties,
              }),
              {
                optimisticData: nextSiblingFile,
                revalidate: false,
              }
            )
          )
        : undefined,
    ]);
    void mutateFiles(() => operations.then(() => nextFiles), {
      optimisticData: nextFiles,
      revalidate: false,
    });
  };
};

type MenuState = Record<string, boolean>;
export const useTreeMenuEffects = ({
  file,
  wiki,
  files,
  closedByDefault,
  addMenu,
  moreMenu,
  move,
}: {
  file: DriveFile | undefined;
  wiki: DriveFile;
  files: DriveFile[];
  closedByDefault: boolean;
  addMenu: PopupState;
  moreMenu: PopupState;
  move: ReturnType<typeof useMove>;
}) => {
  const { data: menuState, mutate: mutateMenu } = useSWR<MenuState>(`/api/menu/${wiki.id}`);
  const { openId, setOpenId } = useOpenId('tree');
  const modal = useStore((s) => s.modalType);
  const siblings = files.filter((f) => f.parents?.[0] === file?.parents?.[0]);
  const fileIndex = sort(siblings).findIndex((o) => o.id === file?.id);

  // move up and down using arrow keys
  useKeyPress('ArrowUp', (e) => {
    if (!e.shiftKey || !e.ctrlKey || !file || modal || openId || file.id === wiki.id) return;
    if (siblings.length === 1 || fileIndex === 0) return;
    e.preventDefault();
    move('up');
  });

  useKeyPress('ArrowDown', (e) => {
    if (!e.shiftKey || !e.ctrlKey || !file || modal || openId || file.id === wiki.id) return;
    if (siblings.length === 1 || fileIndex === siblings.length - 1) return;
    e.preventDefault();
    move('down');
  });

  // open close folders
  useKeyPress(' ', (e) => {
    if (!file || modal || openId || !isAFolder(file)) return;
    const id = getId(file);
    const hasChildren = files.some((o) => o.parents?.[0] === id);
    if (!hasChildren) return;
    e.preventDefault();
    const isOpen = menuState?.[file.id] ?? !closedByDefault;
    void mutateMenu({ ...menuState, [file.id]: !isOpen }, false);
  });

  // open more menu
  useKeyPress('.', () => {
    if (!file || modal || openId) return;
    const activeMenuItem = document.querySelector('[aria-current="page"]')?.parentElement;
    const moreIcon = activeMenuItem?.querySelector('[data-icon="more"]');
    if (!moreIcon) return;
    setOpenId(file.id);
    moreMenu.setAnchorEl(moreIcon);
    moreMenu.open();
  });

  // open add menu
  useKeyPress('a', (e) => {
    if (e.ctrlKey || e.metaKey) return;
    if (!file || modal || openId) return;
    const activeMenuItem = document.querySelector('[aria-current="page"]')?.parentElement;
    const addIcon = activeMenuItem?.querySelector('[data-icon="add"]');
    if (!addIcon) return;
    setOpenId(file.id);
    addMenu.setAnchorEl(addIcon);
    addMenu.open();
  });

  // determines whether or not a files ancestors should be open
  const parent = file?.parents?.[0];
  useEffect(() => {
    if (!parent) return;
    const ancestors = getAllAncestors(files, parent);
    const newMenu = Object.fromEntries(ancestors.map((o) => [o.id, true]));
    if (Object.keys(newMenu).length === 0) return;
    void mutateMenu((prev) => ({ ...prev, ...newMenu }), false);
    // only need to run when parent changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parent, mutateMenu]);
};
